import { useEffect, useState } from 'react';
import { Avatar } from '../components/Avatar';
import { IconMic, IconMicOff, IconPhone, IconPhoneOff, IconSpeaker, IconSwitchCamera, IconVideo, IconVideoOff } from '../components/Icons';
import { formatDuration } from '../lib/format';
import { acceptCall, END_REASON_TEXT, hangup, rejectCall, switchCamera, toggleCamera, toggleMute, toggleSpeaker, useCall } from './callManager';

/** Callback ref: attaches the stream whenever the element (re)mounts or the stream changes. */
const streamRef = (stream: MediaStream | null) => (el: HTMLMediaElement | null) => {
  if (!el || el.srcObject === stream) return;
  el.srcObject = stream;
  if (stream) el.play().catch(() => {});
};

function Timer({ since }: { since: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return <>{formatDuration(Math.max(0, Math.floor((now - since) / 1000)))}</>;
}

export function CallOverlay() {
  const s = useCall();
  if (s.phase === 'idle' || !s.peer) return null;

  const isVideo = s.media === 'video';
  const showRemoteVideo = isVideo && s.remoteStream && s.remoteVideoOn && s.phase === 'active';
  const showLocalVideo = s.hasVideo && s.cameraOn && s.localStream;

  const status =
    s.phase === 'incoming'
      ? isVideo
        ? 'Входящий видеозвонок'
        : 'Входящий аудиозвонок'
      : s.phase === 'outgoing'
        ? s.callId
          ? 'Вызов…'
          : 'Подготовка…'
        : s.phase === 'connecting'
          ? 'Соединение…'
          : s.phase === 'ended'
            ? (END_REASON_TEXT[s.endReason ?? ''] ?? 'Звонок завершён')
            : null;

  return (
    <div className={`call-overlay${showRemoteVideo ? ' with-video' : ''}`} role="dialog" aria-label="Звонок">
      {/* Audio is always played through a dedicated element so it works when the video is hidden. */}
      <audio ref={streamRef(s.remoteStream)} autoPlay />
      {showRemoteVideo ? (
        <video ref={streamRef(s.remoteStream)} className="remote-video" autoPlay playsInline muted />
      ) : (
        <div className="call-peer">
          <Avatar user={s.peer} size={120} />
          <h2>{s.peer.displayName}</h2>
        </div>
      )}

      <div className="call-status">
        {showRemoteVideo && <b>{s.peer.displayName} · </b>}
        {status ?? (s.startedAt ? <Timer since={s.startedAt} /> : null)}
        {s.reconnecting && s.phase !== 'ended' && <span className="warn"> · Восстановление связи…</span>}
        {s.remoteMuted && s.phase === 'active' && <span className="muted"> · микрофон собеседника выключен</span>}
        {s.notice && <div className="call-notice">{s.notice}</div>}
      </div>

      {showLocalVideo && <video ref={streamRef(s.localStream)} className={`local-video${s.phase === 'active' ? '' : ' preview'}`} autoPlay playsInline muted />}

      <div className="call-controls">
        {s.phase === 'incoming' ? (
          <>
            <button className="call-btn reject" onClick={rejectCall} aria-label="Отклонить">
              <IconPhoneOff />
            </button>
            <button className="call-btn accept" onClick={() => void acceptCall()} aria-label="Принять">
              {isVideo ? <IconVideo /> : <IconPhone />}
            </button>
          </>
        ) : s.phase === 'ended' ? null : (
          <>
            <button className={`call-btn${s.muted ? ' off' : ''}`} onClick={toggleMute} aria-label={s.muted ? 'Включить микрофон' : 'Выключить микрофон'}>
              {s.muted ? <IconMicOff /> : <IconMic />}
            </button>
            {s.canToggleSpeaker && (
              <button
                className={`call-btn${s.speakerOn ? ' on' : ''}`}
                onClick={toggleSpeaker}
                aria-label={s.speakerOn ? 'Выключить громкую связь' : 'Включить громкую связь'}
                aria-pressed={s.speakerOn}
              >
                <IconSpeaker />
              </button>
            )}
            {s.hasVideo && (
              <button className={`call-btn${s.cameraOn ? '' : ' off'}`} onClick={toggleCamera} aria-label={s.cameraOn ? 'Выключить камеру' : 'Включить камеру'}>
                {s.cameraOn ? <IconVideo /> : <IconVideoOff />}
              </button>
            )}
            {s.hasVideo && s.canSwitchCamera && (
              <button className="call-btn" onClick={() => void switchCamera()} aria-label="Сменить камеру">
                <IconSwitchCamera />
              </button>
            )}
            <button className="call-btn reject" onClick={hangup} aria-label="Завершить">
              <IconPhoneOff />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
